import React, { useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  AttachFile as AttachFileIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useTaskAttachments } from '../../../../hooks/useTaskAttachments';
import FileExplorer from '../../../../../files/components/FileExplorer';
import { downloadFileLink } from '../../../../../files/hooks/useFileUrl';
import { useFilePicker, useUploadQueue, UploadProgressList } from '../../../../../files/upload';
import { useConfirmDialog } from '../../../../../../hooks/mui/useConfirmDialog';
import { usePermissions } from '../../../../../auth/hooks/usePermissions';

const TaskAttachments = React.memo(function TaskAttachments({ taskId, task, update_task, taskRecurrenceHistory, loadRecurrenceHistory, recurrenceLoading }) {
  const { t } = useTranslation(['common', 'tasks', 'files']);
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();
  const { hasScope } = usePermissions();
  // Attaching and removing change the task; new files also go in the task's folder in Filer
  const canUpdate = hasScope('task:update');
  const canUpload = canUpdate && hasScope('file:upload') && hasScope('file:read');

  const {
    error,
    uploadAndAttachFile,
    removeAttachment,
    clearError,
  } = useTaskAttachments();

  useEffect(() => {
    loadRecurrenceHistory();
  }, [loadRecurrenceHistory]);

  // Files from every occurrence of a recurring task, grouped by occurrence date
  const allAttachments = taskRecurrenceHistory?.chain_tasks
    ?.reduce((acc, chainTask) => {
      const date = chainTask.scheduled_date;
      if (!acc[date]) {
        acc[date] = {
          task_id: chainTask.task_id,
          attachments: []
        };
      }

      chainTask.attachments.forEach(att => {
        acc[date].attachments.push({
          file_id: att.file_id,
          filename: att.file_name,
          filetype: att.filetype,
          file_size: att.file_size,
          uploader_name: att.attached_by?.name || '',
          uploader: att.attached_by,
          uploaded_at: att.attached_at,
          attachment_id: att.attachment_id,
          task_id: chainTask.task_id,
        });
      });

      return acc;
    }, {}) || {};

  const files = Object.entries(allAttachments).map(([date, data]) => ({
    date,
    task_id: data.task_id,
    highlight: date === task?.scheduled_date,
    count: data.attachments.length,
    attachments: data.attachments,
  }));

  // New files go to the task's folder and are attached to this occurrence
  const uploads = useUploadQueue({
    upload: (file, { onUploadProgress }) => uploadAndAttachFile(file, taskId, task?.title, { onUploadProgress }),
    onFinished: (results) => {
      if (!results.length) return;
      update_task();
      loadRecurrenceHistory();
    },
  });
  const picker = useFilePicker({ onFiles: uploads.enqueue });

  // Removes the file from its task; the file itself stays in Filer
  const handleRemove = useCallback((file) => {
    showConfirmDialog({
      title: t('tasks:attachments.delete_title'),
      message: t('tasks:attachments.confirm_delete'),
      confirmText: t('tasks:attachments.remove'),
      severity: 'error',
      onConfirm: async () => {
        const result = await removeAttachment(file.task_id || taskId, file.attachment_id);
        if (result?.success) {
          update_task();
          loadRecurrenceHistory();
        }
      },
    });
  }, [taskId, removeAttachment, t, update_task, loadRecurrenceHistory, showConfirmDialog]);

  const handleFileAction = useCallback((action, file) => {
    if (action === 'download') downloadFileLink(file.file_id, { filename: file.filename });
    if (action === 'delete' || action === 'remove') handleRemove(file);
  }, [handleRemove]);

  if (!task) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  const removeLabel = t('tasks:attachments.remove');

  return (
    <Box sx={{ height: '100%', minHeight: 500, flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {error && (
        <Alert severity="error" onClose={clearError}>
          {error}
        </Alert>
      )}

      {picker.input}

      {/* File Explorer; dropping files on it uploads them to this task */}
      <Box sx={{ height: '90vh', overflow: 'hidden' }}>
        {recurrenceLoading && !taskRecurrenceHistory ? (
          <Box display="flex" alignItems="center" justifyContent="center" height="100%">
            <CircularProgress />
          </Box>
        ) : (
          <FileExplorer
            folders={[]}
            files={files}
            categorizeByDate={true}
            onRefresh={update_task}
            onFileAction={handleFileAction}
            fileActions={canUpdate ? ['download', { key: 'remove', label: removeLabel }] : ['download']}
            deleteLabel={removeLabel}
            enableCompareMode={true}
            enableUploadFile={canUpload}
            onUploadFile={picker.open}
            onFilesDropped={canUpload ? uploads.enqueue : undefined}
            dropLabel={t('tasks:attachments.drop_files')}
            emptyState={
              <Box display="flex" flexDirection="column" alignItems="center" gap={1} py={4} color="text.secondary">
                <AttachFileIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                <Typography variant="body1">{t('tasks:attachments.empty')}</Typography>
                {canUpload && <Typography variant="body2">{t('tasks:attachments.drop_hint')}</Typography>}
              </Box>
            }
          />
        )}
      </Box>

      <ConfirmDialog />
      <UploadProgressList
        variant="floating"
        items={uploads.items}
        onDismiss={uploads.dismiss}
        onClose={uploads.clearFinished}
      />
    </Box>
  );
});

export default TaskAttachments;
